import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  ErrorMessage,
  SecondaryButton,
  PrimaryLoadingButton,
  PrimaryTextField,
  ContentMediaView,
  PrimaryToggleButtonGroup,
  PrimaryToggleButton,
  PrimaryButton,
} from 'components';

import { Grid } from '@mui/material';
import FileDragUploader from 'components/organisms/file-drag-uploader';
import { UploadResponse, UploadScenarios, FileExtensions } from 'interfaces/file-uploader';
import { ICreateContentModalForm } from 'base/common/add-content-modal/interface';
import React from 'react';
import { getFileDataFromUrl } from 'utils/file-uploader';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import GenerateVideoThumbnail from 'base/common/generate-video-thumbnail';
import { BlockType } from '@surge-global-engineering/form-builder-v2';
import { uploadConfig } from 'base/core/hooks/use-file-upload';

interface ContentModalFormProps {
  onSubmit: SubmitHandler<any>;
  closeModal: () => void;
  loading: boolean;
  blockType?: BlockType;
}

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
}

const videos = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
const images = [FileExtensions.jpg, FileExtensions.jpeg, FileExtensions.png];
const documents = [FileExtensions.pdf];
const fonts = [FileExtensions.woff, FileExtensions.woff2];

const extensionToType = new Map<string, string>([]);

videos.forEach((video) => {
  extensionToType.set(video, FileType.VIDEO);
});
images.forEach((image) => {
  extensionToType.set(image, FileType.IMAGE);
});
documents.forEach((document) => {
  extensionToType.set(document, FileType.DOCUMENT);
});
fonts.forEach((font) => {
  extensionToType.set(font, FileType.FONT);
});

const ContentModalForm = ({
  onSubmit, closeModal, loading, blockType,
}: ContentModalFormProps) => {
  const {
    control,
    reset,
    watch,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<ICreateContentModalForm>();
  const [openThumbnailModal, setOpenThumbnailModal] = React.useState(false);
  const [generatedThumbnailUrl, setGeneratedThumbnailUrl] = React.useState('');
  const [requestfileName, setRequestFileName] = React.useState('');
  const [scenario, setScenario] = React.useState<UploadScenarios>(UploadScenarios.expertContent);
  const [allowedExtension, setAllowedExtension] = React.useState('');

  const handleUpload = (res: UploadResponse) => {
    clearErrors('contentUrl');
    setValue('fileType', res.extension.toUpperCase());
    setValue('fileCategory', res.category ?? null);
    setValue('fileName', res.name);
    if (res.statusCode !== 201) {
      setError('contentUrl', { type: 'custom', message: res.message });
      setValue('contentUrl', '');
      setValue('thumbnailGenerated', '');
    } else {
      setValue('contentUrl', res.url);
    }
  };

  const isExternalFile = watch('isExternalFile');
  const mediaUrl = watch('contentUrl');
  const mediaExt = watch('fileType');
  const mediaCategory = watch('fileCategory');

  const getMediaDataFromUrl = async (url: string) => {
    clearErrors('contentUrl');

    try {
      const data = await getFileDataFromUrl(url);
      setValue('fileType', data.fileType);
      setValue('fileCategory', data.fileCategory);
      setValue('fileName', data.fileName);
      setValue('thumbnailGenerated', '');
      setRequestFileName(data.fileName);
    } catch (error: any) {
      setError('contentUrl', { type: 'custom', message: 'File URL is broken or not supported' });
    }
  };

  React.useEffect(() => {
    if (isExternalFile && mediaUrl && !errors.contentUrl) {
      getMediaDataFromUrl(mediaUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalFile, mediaUrl]);

  const handleUrlModeChange = (e: any) => {
    clearErrors();
    reset({
      path: '/root',
      contentUrl: '',
      fileType: '',
      fileCategory: '',
      fileName: '',
      isExternalFile: e.target.value === 'url',
      thumbnailGenerated: '',
    });
  };

  const getScenario = (blockTypeInput?: BlockType): UploadScenarios => (
    blockTypeInput === BlockType.TEXT
      ? UploadScenarios.textBlock
      : UploadScenarios.expertContent
  );

  React.useEffect(() => {
    const updatedScenario = getScenario(blockType);
    setScenario(updatedScenario);
    const updatedAllowedExtension = (uploadConfig.get(scenario)?.allowedExtensions as string[]).join(',');
    setAllowedExtension(updatedAllowedExtension);
  }, [blockType, scenario]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {openThumbnailModal && mediaUrl && (
        <GenerateVideoThumbnail
          open={openThumbnailModal}
          onClose={() => setOpenThumbnailModal(false)}
          videoUrl={mediaUrl}
          videoExt={mediaExt.replace('.', '').toLowerCase()}
          onFileCreate={({ fileName, url }) => {
            setValue('thumbnailGenerated', fileName);
            setGeneratedThumbnailUrl(url);
            clearErrors('thumbnailGenerated');
            setOpenThumbnailModal(false);
          }}
        />
      )}
      <div className="mb-3 mt-1">
        <PrimaryToggleButtonGroup
          value={isExternalFile ? 'url' : 'file'}
          fullWidth
          exclusive
          onChange={handleUrlModeChange}
          aria-label="Upload media toggle"
        >
          <PrimaryToggleButton value="file">Click to upload a file</PrimaryToggleButton>
          <PrimaryToggleButton value="url">Click to enter a url</PrimaryToggleButton>
        </PrimaryToggleButtonGroup>
      </div>
      {isExternalFile ? (
        <div>
          {mediaUrl && !errors?.contentUrl && (
            <div className="file-upload-content-media-view d-flex align-items-center justify-content-center mb-2">
              <ContentMediaView url={mediaUrl} mediaType={mediaCategory ?? ''} thumbnail={generatedThumbnailUrl} fileName={requestfileName} />
            </div>
          )}
          <div className="row">
            <div className="col-12 col-md-9 col-xl-10 pe-lg-1">
              <Controller
                control={control}
                name="contentUrl"
                render={({ field }) => <PrimaryTextField {...field} sx={{ mt: 0 }} placeholder="Media file URL" />}
              />
            </div>
            <div className="col-12 col-md-3 col-xl-2 ps-lg-1 mt-2 mt-md-0">
              <PrimaryButton
                fullWidth
                onClick={() => setOpenThumbnailModal(true)}
                disabled={!mediaUrl || mediaUrl === '' || (mediaUrl && mediaCategory !== FileType.VIDEO)}
              >
                <PhotoSizeSelectLargeIcon fontSize="small" />
              </PrimaryButton>
            </div>
          </div>
          <div className="mb-3 mt-1">
            {errors.contentUrl && <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>}
            {!errors.contentUrl && errors.thumbnailGenerated
              && <ErrorMessage>{errors?.thumbnailGenerated?.message}</ErrorMessage>}
          </div>
        </div>
      ) : (
        <div className="form-item mb-1">
          <Controller
            control={control}
            name="contentUrl"
            render={({ field }) => (
              <FileDragUploader
                textLabel="click or drag a file to this area to upload"
                handleFile={handleUpload}
                scenario={scenario}
                currentContentUrl=""
                uploadAnotherText="Replace media"
                currentMediaType=""
                accept={allowedExtension}
              />
            )}
          />
          <div className="mb-3 mt-1">
            {errors.contentUrl && <ErrorMessage>{errors?.contentUrl?.message}</ErrorMessage>}
          </div>
        </div>
      )}
      <div className="form-item">
        <Controller
          control={control}
          name="title"
          render={({ field }) => <PrimaryTextField {...field} label="Title " />}
        />
        <ErrorMessage>{errors?.title?.message}</ErrorMessage>
      </div>
      <div className="form-item">
        <Controller
          control={control}
          name="description"
          render={({ field }) => <PrimaryTextField multiline {...field} label="Description" />}
        />
        <ErrorMessage>{errors?.description?.message}</ErrorMessage>
      </div>

      <Grid container spacing={3}>
        <Grid item xs>
          <SecondaryButton fullWidth onClick={closeModal}>
            Cancel
          </SecondaryButton>
        </Grid>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} fullWidth type="submit">
            Submit
          </PrimaryLoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

ContentModalForm.defaultProps = {
  blockType: undefined,
};

export default ContentModalForm;
